if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.variantMediaPreloadQueued = false;
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.queueVariantMediaPreload();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel')),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          window?.enableZoomOnHover?.(2, newNode);
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        if (!shouldSwapProduct) {
          const variant = this.findLocalVariant();
          if (variant) {
            this.applyImmediateVariantUpdate(variant, productUrl);
          }
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getLocalVariantData() {
        if (this.localVariantData) return this.localVariantData;

        const variantJson = this.querySelector('[data-variant-json]');
        if (!variantJson) return [];

        try {
          this.localVariantData = JSON.parse(variantJson.textContent);
        } catch (error) {
          console.error('Unable to parse local variant JSON', error);
          this.localVariantData = [];
        }

        return this.localVariantData;
      }

      getSelectedOptionLabels() {
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return [];

        return Array.from(variantSelects.querySelectorAll('select, fieldset')).map((input) => {
          if (input.tagName === 'SELECT') return input.value;
          return Array.from(input.querySelectorAll('input')).find((radio) => radio.checked)?.value;
        });
      }

      findLocalVariant() {
        const selectedOptions = this.getSelectedOptionLabels();
        if (!selectedOptions.length || selectedOptions.some((value) => typeof value === 'undefined')) return null;

        return (
          this.getLocalVariantData().find((variant) => {
            if (!Array.isArray(variant.options) || variant.options.length !== selectedOptions.length) return false;
            return variant.options.every((option, index) => option === selectedOptions[index]);
          }) || null
        );
      }

      applyImmediateVariantUpdate(variant, productUrl) {
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant?.available) {
          this.productForm?.toggleSubmitButton(true, window.variantStrings.soldOut);
        } else {
          this.productForm?.toggleSubmitButton(false);
        }

        if (variant?.featured_media?.id) {
          this.activateMediaById(variant.featured_media.id);
        }
      }

      queueVariantMediaPreload() {
        if (this.variantMediaPreloadQueued) return;
        this.variantMediaPreloadQueued = true;

        const run = () => this.preloadVariantFeaturedMedia();
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(run, { timeout: 1200 });
          return;
        }

        window.setTimeout(run, 180);
      }

      preloadVariantFeaturedMedia() {
        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery) return;

        const mediaIds = [
          ...new Set(
            this.getLocalVariantData()
              .map((variant) => variant?.featured_media?.id)
              .filter(Boolean),
          ),
        ];

        mediaIds.forEach((mediaId, index) => {
          const mediaItem = mediaGallery.querySelector(`[data-media-id="${this.dataset.section}-${mediaId}"]`);
          const image = mediaItem?.querySelector('.product__media img, .deferred-media__poster img, img');
          if (!image) return;

          image.loading = 'eager';
          image.decoding = 'async';
          image.fetchPriority = index < 2 ? 'high' : 'low';

          const src = image.currentSrc || image.src;
          const srcset = image.getAttribute('srcset');
          const sizes = image.getAttribute('sizes');
          if (!src && !srcset) return;
          if (image.dataset.rvPrewarmed === 'true') return;
          image.dataset.rvPrewarmed = 'true';

          const preload = new Image();
          if (srcset) preload.srcset = srcset;
          if (sizes) preload.sizes = sizes;
          if (src) preload.src = src;
          preload.decoding = 'async';
          preload.loading = 'eager';
          preload.fetchPriority = index < 2 ? 'high' : 'low';

          if (preload.complete && preload.naturalWidth > 0 && typeof preload.decode === 'function') {
            preload.decode().catch(() => {});
          } else {
            preload.addEventListener(
              'load',
              () => {
                if (typeof preload.decode === 'function') preload.decode().catch(() => {});
              },
              { once: true },
            );
          }
        });
      }

      activateMediaById(featuredMediaId) {
        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery || !featuredMediaId) return;

        const targetMediaId = `${this.dataset.section}-${featuredMediaId}`;
        const targetMedia = mediaGallery.querySelector(`[data-media-id="${targetMediaId}"]`);
        if (!targetMedia) return;

        mediaGallery.setActiveMedia?.(targetMediaId, false);
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const shouldFreezeMedia = this.shouldFreezeMediaDuringPersonalization();

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          if (!shouldFreezeMedia) {
            this.updateMedia(html, variant?.featured_media?.id);
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut,
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      shouldFreezeMediaDuringPersonalization() {
        const activeMedia = this.querySelector('media-gallery [data-media-id].is-active');
        if (!activeMedia) return false;

        const hasPersonalizationCanvas = !!activeMedia.querySelector(
          '.cl-canvas-container, .canvas-container, #preview-canvas, canvas.upper-canvas, canvas.lower-canvas',
        );

        if (!hasPersonalizationCanvas) return false;

        const customilyOptions = this.querySelector('#customily-options');
        if (!customilyOptions) return true;

        const activeEditor = customilyOptions.querySelector(
          '.customily-options-wrapper.show, .customily-options-wrapper.collapse.show',
        );

        if (!activeEditor) return false;

        const isVisible = (element) => {
          if (!element) return false;
          const style = window.getComputedStyle(element);
          if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
          return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
        };

        const editTitle = activeEditor.querySelector('.customily-edit-personalization-title');
        const closeButton = activeEditor.querySelector('.customily-edit-personalization-close');

        return isVisible(activeEditor) && (isVisible(editTitle) || isVisible(closeButton));
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      async updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallery = this.querySelector('media-gallery');
        const mediaGallerySource = mediaGallery?.querySelector('ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);
        const targetMediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;

        if (mediaGallery?.querySelector(`[data-media-id="${targetMediaId}"]`)) {
          const sourceIds = mediaGallerySource
            ? Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]')).map((item) => item.dataset.mediaId)
            : [];
          const destinationIds = mediaGalleryDestination
            ? Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]')).map(
                (item) => item.dataset.mediaId,
              )
            : [];

          const sameGalleryOrder =
            sourceIds.length > 0 &&
            sourceIds.length === destinationIds.length &&
            sourceIds.every((mediaId, index) => mediaId === destinationIds[index]);

          if (sameGalleryOrder) {
            await mediaGallery?.setActiveMedia?.(targetMediaId, false);

            const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
            const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
            if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;

            this.queueVariantMediaPreload();
            return;
          }
        }

        const primeImage = (container, selectors = '.product__media img, .deferred-media__poster img, img') => {
          const image = container?.querySelector(selectors);
          if (!image) return null;
          image.loading = 'eager';
          image.decoding = 'sync';
          image.fetchPriority = 'high';
          return image;
        };

        const preloadImageElement = async (image) => {
          if (!image) return;

          const src = image.currentSrc || image.src;
          const srcset = image.getAttribute('srcset');
          const sizes = image.getAttribute('sizes');
          if (!src && !srcset) return;

          const preload = new Image();
          if (srcset) preload.srcset = srcset;
          if (sizes) preload.sizes = sizes;
          if (src) preload.src = src;
          preload.decoding = 'sync';
          preload.loading = 'eager';
          preload.fetchPriority = 'high';

          if (preload.complete && preload.naturalWidth > 0) {
            if (typeof preload.decode === 'function') {
              try {
                await preload.decode();
              } catch (e) {}
            }
            return;
          }

          await new Promise((resolve) => {
            const done = () => {
              if (typeof preload.decode === 'function') {
                preload
                  .decode()
                  .catch(() => {})
                  .finally(resolve);
                return;
              }
              resolve();
            };
            preload.addEventListener('load', done, { once: true });
            preload.addEventListener('error', resolve, { once: true });
          });
        };

        const preloadVariantMedia = async (container) => {
          if (!container) return;
          const primaryImage = primeImage(container);
          const thumbnailButton = html.querySelector(`media-gallery [data-target="${targetMediaId}"]`);
          const thumbnailImage = primeImage(thumbnailButton, 'img');
          await Promise.all([preloadImageElement(primaryImage), preloadImageElement(thumbnailImage)]);
        };

        const cancelScrollReveal = (container) => {
          if (!container) return;
          if (container.matches?.('.scroll-trigger')) {
            container.classList.add('scroll-trigger--cancel');
            container.classList.remove('scroll-trigger--offscreen');
          }
          container.querySelectorAll('.scroll-trigger').forEach((element) => {
            element.classList.add('scroll-trigger--cancel');
            element.classList.remove('scroll-trigger--offscreen');
          });
        };

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;
          const pendingRemovalItems = [];

          const destinationTargetMedia = mediaGalleryDestination.querySelector(`[data-media-id="${targetMediaId}"]`);
          cancelScrollReveal(destinationTargetMedia);
          await preloadVariantMedia(destinationTargetMedia);

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              cancelScrollReveal(mediaGalleryDestinationItems[i]);
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              pendingRemovalItems.push(mediaGallerySourceItems[i]);
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);
            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
              );
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });

          const sourceTargetMedia = mediaGallerySource.querySelector(`[data-media-id="${targetMediaId}"]`);
          cancelScrollReveal(sourceTargetMedia);
          primeImage(sourceTargetMedia);

          mediaGallery?.querySelectorAll('.product__media-item').forEach((item) => cancelScrollReveal(item));
          await mediaGallery?.setActiveMedia?.(targetMediaId, false);

          pendingRemovalItems.forEach((item) => {
            if (item?.isConnected) item.remove();
          });

          if (pendingRemovalItems.length) {
            mediaGallery.querySelector('[id^="GalleryViewer"]')?.resetPages?.();
          }
        } else {
          mediaGallery?.querySelectorAll('.product__media-item').forEach((item) => cancelScrollReveal(item));
          await mediaGallery?.setActiveMedia?.(targetMediaId, false);
        }

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;

        this.queueVariantMediaPreload();
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list',
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}
